import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { clearSession } from '@app/constants/app/storage';
import { forgotPasswordPath, signInPath } from '@app/constants/url';
import { useResetPassword } from '@data';
import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { AuthLayout, useEnterFrom } from './AuthLayout';

// Matches the minimum the API enforces, so the form says so before the request is made.
const MINIMUM_PASSWORD_LENGTH = 8;

/**
 * Step two of a password reset: the screen the emailed link opens, carrying `?resetToken=`.
 */
export const ResetPasswordPage = (): ReactElement => {
  const { t } = useTranslation();
  const enterFrom = useEnterFrom();
  const [searchParams] = useSearchParams();
  // Not `token`: that param name is already how the app receives a refresh token to exchange.
  const token = searchParams.get('resetToken');

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isDone, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { mutate: resetPassword, isPending } = useResetPassword();

  const requestNewLink = (
    <Link
      to={forgotPasswordPath}
      state={{ enterFrom: 'left' }}
      className="text-sm font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
    >
      {t('passwordReset.requestNewLink')}
    </Link>
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password.length < MINIMUM_PASSWORD_LENGTH) {
      setError(t('passwordReset.tooShort', { minimum: MINIMUM_PASSWORD_LENGTH }));
      return;
    }
    if (password !== confirmation) {
      setError(t('passwordReset.mismatch'));
      return;
    }

    resetPassword(
      { token: token!, password },
      {
        onSuccess: () => {
          // No session is handed out here on purpose, so the next step is signing in with the new
          // password. Any session this browser still holds is dropped for the same reason: the API
          // has already deleted the refresh token, and a leftover access token would carry someone
          // who reset from a signed-in tab straight past the sign-in this screen promises.
          clearSession();
          setDone(true);
        },
        onError: err => {
          const reason = (err as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
          setError(reason === 'invalidResetToken' ? t('passwordReset.linkExpired') : t('passwordReset.resetFailed'));
        },
      },
    );
  };

  if (isDone) {
    return (
      <AuthLayout enterFrom={enterFrom} backTo={signInPath}>
        <h1 className="mb-3 text-3xl font-bold tracking-tight text-foreground">{t('passwordReset.doneTitle')}</h1>
        <p className="mb-6 text-sm text-muted-foreground">{t('passwordReset.doneHint')}</p>
        {/* A full load rather than a route change: the app reads the stored token once on mount, so
            it has to start over to notice the session that was just cleared. */}
        <Button className="w-full" onClick={() => window.location.assign(signInPath)}>
          {t('passwordReset.signIn')}
        </Button>
      </AuthLayout>
    );
  }

  // Someone landed here without following a mail, or the link lost its tail on the way.
  if (!token) {
    return (
      <AuthLayout enterFrom={enterFrom} backTo={signInPath}>
        <h1 className="mb-3 text-3xl font-bold tracking-tight text-foreground">{t('passwordReset.invalidLink')}</h1>
        <p className="mb-6 text-sm text-muted-foreground">{t('passwordReset.invalidLinkHint')}</p>
        <div className="flex justify-center">{requestNewLink}</div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout enterFrom={enterFrom} backTo={signInPath}>
      <h1 className="mb-3 text-3xl font-bold tracking-tight text-foreground">{t('passwordReset.chooseTitle')}</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        {t('passwordReset.chooseHint', { minimum: MINIMUM_PASSWORD_LENGTH })}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="reset-password" className="sr-only">
            {t('passwordReset.newPassword')}
          </Label>
          <Input
            id="reset-password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={t('passwordReset.newPassword')}
            autoComplete="new-password"
            className="bg-popover"
            required
            autoFocus
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="reset-password-confirmation" className="sr-only">
            {t('passwordReset.confirmPassword')}
          </Label>
          <Input
            id="reset-password-confirmation"
            type="password"
            value={confirmation}
            onChange={e => setConfirmation(e.target.value)}
            placeholder={t('passwordReset.confirmPassword')}
            autoComplete="new-password"
            className="bg-popover"
            required
          />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" loading={isPending} className="w-full">
          {t('passwordReset.save')}
        </Button>
        <div className="flex justify-center">{requestNewLink}</div>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
